import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import Layout from './Layout';
import { useAuth } from '../contexts/AuthContext';
import { getApiUrl } from '../config/api';
import environment from '../config/environment';

const ArmamentoSupervision = () => {
  const { user } = useAuth();
  const [paso, setPaso] = useState('seleccionar'); // seleccionar | captura
  const [loadingPuestos, setLoadingPuestos] = useState(true);
  const [error, setError] = useState('');
  const [puestos, setPuestos] = useState([]);
  const [selectedPuesto, setSelectedPuesto] = useState('');
  const [fotoFrente, setFotoFrente] = useState(null);
  const [fotoFrentePreview, setFotoFrentePreview] = useState('');
  const [fotoReverso, setFotoReverso] = useState(null);
  const [fotoReversoPreview, setFotoReversoPreview] = useState('');
  const [simulando, setSimulando] = useState(false);
  const [resultadoSimulacion, setResultadoSimulacion] = useState(null);
  const [showResultadoModal, setShowResultadoModal] = useState(false);
  const [showErrorSerieModal, setShowErrorSerieModal] = useState(false);
  const [serieDesdeFrente, setSerieDesdeFrente] = useState(null);
  const [analisisFrenteEnCurso, setAnalisisFrenteEnCurso] = useState(false);
  const frentePromiseRef = useRef(null);

  // Solo supervisores pueden acceder
  const isSupervisor = user?.rol === 'S';

  const fetchPuestosArmamento = useCallback(
    async (silent = false) => {
      if (!isSupervisor) {
        if (!silent) setLoadingPuestos(false);
        return;
      }

      try {
        if (!silent) {
          setLoadingPuestos(true);
          setError('');
        }

        let lat = null;
        let lng = null;

        try {
          const configRes = await fetch(getApiUrl('SUPERVISION', 'CONFIG'), {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include'
          });
          const configData = await configRes.json();
          if (configRes.ok && configData.data?.simulatedGPS?.enabled) {
            lat = configData.data.simulatedGPS.lat;
            lng = configData.data.simulatedGPS.lng;
          }
        } catch (_) {}

        if (lat == null || lng == null) {
          const coords = await new Promise((resolve) => {
            if (!navigator.geolocation) {
              resolve(null);
              return;
            }
            navigator.geolocation.getCurrentPosition(
              (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
              () => resolve(null),
              { timeout: 8000, maximumAge: 60000 }
            );
          });
          if (coords) {
            lat = coords.lat;
            lng = coords.lng;
          }
        }

        const url = new URL(getApiUrl('SUPERVISION', 'PUESTOS_ARMAMENTO'));
        url.searchParams.set('username', user?.username || '');
        url.searchParams.set('appid', user?.appid || '');
        if (lat != null && lng != null) {
          url.searchParams.set('lat', String(lat));
          url.searchParams.set('lng', String(lng));
        }

        const response = await fetch(url.toString(), {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include'
        });

        const data = await response.json();

        if (response.ok && data.success) {
          setPuestos(data.data || []);
        } else if (!silent) {
          setError(data.error || 'Error al cargar los puestos de servicio');
        }
      } catch (err) {
        if (!silent) {
          console.error('Error cargando puestos para armamento:', err);
          setError('Error de conexión al cargar los puestos de servicio');
        }
      } finally {
        if (!silent) setLoadingPuestos(false);
      }
    },
    [isSupervisor, user]
  );

  useEffect(() => {
    fetchPuestosArmamento(false);
  }, [fetchPuestosArmamento]);

  const puestoSeleccionadoInfo = useMemo(
    () => puestos.find((p) => String(p.id) === String(selectedPuesto)) || null,
    [puestos, selectedPuesto]
  );

  const handleSeleccionPuesto = (e) => {
    const value = e.target.value;
    setSelectedPuesto(value);
    setResultadoSimulacion(null);

    if (value) {
      setPaso('captura');
      setError('');
    } else {
      setPaso('seleccionar');
    }
  };

  const handleFotoFrenteChange = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) {
      return;
    }

    setFotoFrente(file);
    setResultadoSimulacion(null);
    setSerieDesdeFrente(null);
    setAnalisisFrenteEnCurso(true);
    frentePromiseRef.current = null;

    const reader = new FileReader();
    reader.onloadend = () => {
      setFotoFrentePreview(typeof reader.result === 'string' ? reader.result : '');
    };
    reader.readAsDataURL(file);

    // Análisis en segundo plano: extraer serie del frente (sin bloquear al usuario)
    const analizarFrente = async () => {
      try {
        const compressed = await compressImage(file);
        const formData = new FormData();
        formData.append('foto', compressed);
        const res = await fetch(getApiUrl('SUPERVISION', 'ARMAMENTO_ANALIZAR_IMAGEN'), {
          method: 'POST',
          body: formData,
          credentials: 'include'
        });
        const data = await res.json();
        const serie = data.success && data.serie ? data.serie : null;
        setSerieDesdeFrente(serie);
        return serie;
      } catch (err) {
        console.warn('Error analizando foto frente en segundo plano:', err);
        setSerieDesdeFrente(null);
        return null;
      } finally {
        setAnalisisFrenteEnCurso(false);
      }
    };
    const promise = analizarFrente();
    frentePromiseRef.current = promise;
  };

  const handleFotoReversoChange = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) {
      return;
    }

    setFotoReverso(file);
    setResultadoSimulacion(null);

    const reader = new FileReader();
    reader.onloadend = () => {
      setFotoReversoPreview(typeof reader.result === 'string' ? reader.result : '');
    };
    reader.readAsDataURL(file);
  };

  const resetFoto = () => {
    setFotoFrente(null);
    setFotoFrentePreview('');
    setFotoReverso(null);
    setFotoReversoPreview('');
    setResultadoSimulacion(null);
    setSerieDesdeFrente(null);
    setAnalisisFrenteEnCurso(false);
    frentePromiseRef.current = null;
  };

  const resetFormulario = () => {
    setSelectedPuesto('');
    setPaso('seleccionar');
    resetFoto();
    setResultadoSimulacion(null);
    setShowResultadoModal(false);
    setShowErrorSerieModal(false);
    setError('');
  };

  // Compresión básica de imágenes en el frontend para reducir peso sin perder mucha calidad
  const compressImage = (file, maxWidth = 1280, maxHeight = 1280, quality = 0.8) => {
    return new Promise((resolve, reject) => {
      try {
        const image = new Image();
        image.onload = () => {
          let { width, height } = image;
          const ratio = Math.min(maxWidth / width, maxHeight / height, 1);
          width = Math.round(width * ratio);
          height = Math.round(height * ratio);

          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            resolve(file);
            return;
          }

          ctx.drawImage(image, 0, 0, width, height);

          canvas.toBlob(
            (blob) => {
              if (!blob) {
                resolve(file);
                return;
              }
              const compressedFile = new File([blob], file.name.replace(/\.[^.]+$/, '.jpg'), {
                type: 'image/jpeg',
                lastModified: Date.now()
              });
              resolve(compressedFile);
            },
            'image/jpeg',
            quality
          );
        };
        image.onerror = () => resolve(file);
        image.src = URL.createObjectURL(file);
      } catch (err) {
        console.warn('Error al comprimir imagen, se enviará original:', err);
        resolve(file);
      }
    });
  };

  const handleSimularVerificacion = async () => {
    setError('');

    if (!selectedPuesto || !fotoFrente || !fotoReverso) {
      setError('Selecciona el punto y sube ambas fotos (frente y reverso).');
      return;
    }

    setSimulando(true);

    try {
      let serie = serieDesdeFrente;
      if (analisisFrenteEnCurso && frentePromiseRef.current) {
        serie = await frentePromiseRef.current;
      }
      if (!serie) {
        const compressedReverso = await compressImage(fotoReverso);
        const fd = new FormData();
        fd.append('foto', compressedReverso);
        const resReverso = await fetch(getApiUrl('SUPERVISION', 'ARMAMENTO_ANALIZAR_IMAGEN'), {
          method: 'POST',
          body: fd,
          credentials: 'include'
        });
        const dataReverso = await resReverso.json();
        serie = dataReverso.success && dataReverso.serie ? dataReverso.serie : null;
      }
      if (!serie) {
        setShowErrorSerieModal(true);
        setSimulando(false);
        return;
      }

      const [compressedFrente, compressedReverso] = await Promise.all([
        compressImage(fotoFrente),
        compressImage(fotoReverso)
      ]);
      const formData = new FormData();
      formData.append('serie_detectada', serie);
      formData.append('foto_frente', compressedFrente);
      formData.append('foto_reverso', compressedReverso);
      formData.append('puntoId', selectedPuesto);
      formData.append(
        'puntoCodigo',
        `${environment.puntoChar}${puestoSeleccionadoInfo?.id_punto || puestoSeleccionadoInfo?.id || ''}`
      );
      formData.append(
        'puntoNombre',
        puestoSeleccionadoInfo?.nombre_cliente ||
          puestoSeleccionadoInfo?.nombre ||
          puestoSeleccionadoInfo?.nombre_punto ||
          ''
      );
      formData.append('username', user?.username || '');

      const response = await fetch(getApiUrl('SUPERVISION', 'ARMAMENTO_REGISTRAR_CON_SERIE'), {
        method: 'POST',
        body: formData,
        credentials: 'include'
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        setError(data.error || 'Error al verificar el armamento.');
        setResultadoSimulacion(null);
        setSimulando(false);
        return;
      }

      const existeEnArmamento = Boolean(data.existeEnArmamento);
      const detalleArmamento = data.detalleArmamento || null;

      setResultadoSimulacion({
        serieDetectada: data.serieDetectada || serie,
        existeEnArmamento,
        puestoNombre:
          puestoSeleccionadoInfo?.nombre ||
          puestoSeleccionadoInfo?.nombre_punto ||
          `Puesto ${selectedPuesto}`,
        detalleArmamento
      });
      setShowResultadoModal(true);
      fetchPuestosArmamento(true);
    } catch (e) {
      console.error('Error verificando armamento:', e);
      setError('Error de conexión al verificar el armamento.');
      setResultadoSimulacion(null);
    } finally {
      setSimulando(false);
    }
  };

  if (!user) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800">No hay usuario autenticado.</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (!isSupervisor) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800">
              No tienes permisos para acceder a esta sección. Solo los supervisores pueden acceder.
            </p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 space-y-6">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Control de Armamento</h1>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 sm:p-4 text-sm text-red-800">
            {error}
          </div>
        )}

        {/* Spinner unificado: "Analizando permiso…" */}
        {simulando && (
          <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40">
            <div className="bg-white rounded-xl shadow-xl px-6 py-5 flex flex-col items-center gap-3">
              <svg
                className="animate-spin h-8 w-8 text-indigo-600"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              <span className="text-sm font-medium text-gray-700">Analizando permiso…</span>
            </div>
          </div>
        )}

        {/* Vista 1: solo selector de punto */}
        {paso === 'seleccionar' && (
          <section className="bg-white border border-gray-200 rounded-xl p-4 sm:p-5 shadow-sm space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-sm font-semibold text-gray-900">Paso 1 · Selecciona el puesto de servicio</h2>
              </div>
            </div>

            {loadingPuestos ? (
              <div className="flex items-center justify-center py-8">
                <svg className="animate-spin h-5 w-5 text-indigo-600 mr-2" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                <span className="text-sm text-gray-600">Cargando puntos…</span>
              </div>
            ) : puestos.length === 0 ? (
              <p className="text-sm text-gray-600">
                No se encontraron puntos disponibles para el día y turno actual.
              </p>
            ) : (
              <>
                <div className="space-y-2">
                  <label htmlFor="puesto" className="block text-xs font-medium text-gray-700">
                    Puesto de servicio
                  </label>
                  <select
                    id="puesto"
                    value={selectedPuesto}
                    onChange={handleSeleccionPuesto}
                    className="mt-1 block w-full pl-3 pr-10 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white"
                  >
                    <option value="">Seleccione un punto…</option>
                    {puestos.map((puesto) => (
                      <option key={puesto.id} value={puesto.id}>
                        {puesto.nombre || puesto.nombre_punto || `Puesto ${puesto.id_punto || puesto.id}`}
                      </option>
                    ))}
                  </select>
                </div>

                {puestoSeleccionadoInfo && (
                  <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <h3 className="text-sm font-semibold text-red-600 mb-1">
                      Punto: {environment.puntoChar}
                      {puestoSeleccionadoInfo.id_punto || puestoSeleccionadoInfo.id || ''}
                    </h3>
                    <h2 className="text-base font-semibold text-indigo-700 mb-1">
                      {puestoSeleccionadoInfo.nombre_cliente || puestoSeleccionadoInfo.nombre || puestoSeleccionadoInfo.nombre_punto}
                    </h2>
                    <p className="text-sm font-medium text-green-600">
                      {puestoSeleccionadoInfo.descripcion || 'Sin descripción'}
                    </p>
                  </div>
                )}
              </>
            )}
          </section>
        )}

        {/* Vista 2: solo captura de fotos y verificación */}
        {paso === 'captura' && (
          <section className="bg-white border border-gray-200 rounded-xl p-4 sm:p-5 shadow-sm space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-sm font-semibold text-gray-900">Paso 2 · Captura la foto del Permiso de Armas</h2>
                <p className="text-xs text-gray-500 mt-1">
                  Primero sube la foto del frente y luego del reverso.
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setPaso('seleccionar');
                  resetFoto();
                }}
                className="text-xs text-indigo-600 hover:text-indigo-800 font-medium"
              >
                Cambiar punto
              </button>
            </div>

            {puestoSeleccionadoInfo && (
              <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                <span className="text-sm text-gray-700">
                  <span className="font-semibold text-indigo-700">
                    {environment.puntoChar}
                    {puestoSeleccionadoInfo.id_punto || puestoSeleccionadoInfo.id || ''}
                  </span>
                  {' · '}
                  {puestoSeleccionadoInfo.nombre_cliente || puestoSeleccionadoInfo.nombre || puestoSeleccionadoInfo.nombre_punto}
                </span>
              </div>
            )}

            <div className="space-y-3">
              {/* Paso 2a: solo pedir frente */}
              {!fotoFrente && (
                <div>
                  <p className="text-xs font-semibold text-gray-700 mb-1">Foto frente (obligatoria)</p>
                  <div className="flex flex-col items-center justify-center gap-3">
                    <label
                      htmlFor="foto-armamento-frente-input"
                      className="px-6 py-3 rounded-xl bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 shadow cursor-pointer"
                    >
                      Capturar Cara frontal
                    </label>
                    <input
                      id="foto-armamento-frente-input"
                      type="file"
                      accept="image/*"
                      capture="environment"
                      className="hidden"
                      onChange={handleFotoFrenteChange}
                    />
                  </div>
                </div>
              )}

              {/* Paso 2b: solo pedir reverso (nada de frente) */}
              {fotoFrente && !fotoReverso && (
                <div>
                  <p className="text-xs font-semibold text-gray-700 mb-1">Foto reverso (obligatoria)</p>
                  <div className="flex flex-col items-center justify-center gap-3">
                    <label
                      htmlFor="foto-armamento-reverso-input"
                      className="px-6 py-3 rounded-xl bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 shadow cursor-pointer"
                    >
                      Capturar Cara Posterior
                    </label>
                    <input
                      id="foto-armamento-reverso-input"
                      type="file"
                      accept="image/*"
                      capture="environment"
                      className="hidden"
                      onChange={handleFotoReversoChange}
                    />
                  </div>
                </div>
              )}

              {/* Paso 2c: ambas cargadas → solo las dos imágenes + botón Verificar */}
              {fotoFrente && fotoReverso && (
                <>
                  <div className="space-y-4">
                    <div className="relative rounded-xl overflow-hidden border border-gray-200 bg-gray-50">
                      <img
                        src={fotoFrentePreview}
                        alt="Frente"
                        className="w-full h-52 object-contain bg-gray-100"
                      />
                    </div>
                    <div className="relative rounded-xl overflow-hidden border border-gray-200 bg-gray-50">
                      <img
                        src={fotoReversoPreview}
                        alt="Reverso"
                        className="w-full h-52 object-contain bg-gray-100"
                      />
                    </div>
                  </div>
                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={handleSimularVerificacion}
                      disabled={simulando}
                      className="inline-flex items-center justify-center px-4 py-2 rounded-lg text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-300 disabled:cursor-not-allowed"
                    >
                      Verificar serie de armamento
                    </button>
                  </div>
                </>
              )}
            </div>
          </section>
        )}

        {/* Modal cuando no se pudo leer la serie (mismo estilo que resultado) */}
        {showErrorSerieModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
            <div className="bg-white rounded-xl shadow-xl max-w-lg w-full mx-4 p-5">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Resultado de la verificación</h3>
              <div className="rounded-lg border border-red-200 bg-red-50 text-red-800 px-3 py-3 text-sm mb-4">
                <p className="font-semibold mb-1">No se pudo leer la serie del permiso.</p>
                <p>Vuelve a tomar las fotos del frente y reverso.</p>
              </div>
              <div className="flex flex-col sm:flex-row sm:justify-end sm:space-x-3 space-y-2 sm:space-y-0">
                <button
                  type="button"
                  onClick={() => {
                    setShowErrorSerieModal(false);
                    resetFoto();
                    setPaso('captura');
                  }}
                  className="w-full sm:w-auto px-4 py-2 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Volver a tomar foto
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowErrorSerieModal(false);
                    resetFoto();
                  }}
                  className="w-full sm:w-auto px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700"
                >
                  Aceptar
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal de resultado y confirmación (Paso 3) */}
        {showResultadoModal && resultadoSimulacion && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
            <div className="bg-white rounded-xl shadow-xl max-w-lg w-full mx-4 p-5">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Resultado de la verificación</h3>
              

              <div
                className={`rounded-lg border px-3 py-3 text-sm mb-4 ${
                  resultadoSimulacion.existeEnArmamento
                    ? 'border-green-200 bg-green-50 text-green-800'
                    : 'border-amber-200 bg-amber-50 text-amber-900'
                }`}
              >
                <p className="font-semibold mb-1">
                  Armamento:{' '}
                  <span className="font-mono break-all">
                    {resultadoSimulacion.serieDetectada}
                  </span>
                </p>
                <p className="mb-1">
                  Punto:{' '}
                  <span className="font-medium">
                    {environment.puntoChar}
                    {puestoSeleccionadoInfo?.id_punto || puestoSeleccionadoInfo?.id || ''} ·{' '}
                    {puestoSeleccionadoInfo?.nombre_cliente ||
                      puestoSeleccionadoInfo?.nombre ||
                      puestoSeleccionadoInfo?.nombre_punto}
                  </span>
                </p>
                <p className="mt-2">
                  {resultadoSimulacion.existeEnArmamento ? (
                    (() => {
                      const mismoPunto =
                        resultadoSimulacion.detalleArmamento &&
                        String(resultadoSimulacion.detalleArmamento.id_generado || '') ===
                          String(puestoSeleccionadoInfo?.id || puestoSeleccionadoInfo?.id_generado || '');

                      if (mismoPunto) {
                        // Caso OK (mismo punto)
                        return (
                          <>
                            El armamento con serie{' '}
                            <span className="font-mono">{resultadoSimulacion.serieDetectada}</span> del punto{' '}
                            <span className="font-medium">
                              {environment.puntoChar}
                              {puestoSeleccionadoInfo?.id_punto || puestoSeleccionadoInfo?.id || ''}{' '}
                              {puestoSeleccionadoInfo?.nombre_cliente ||
                                puestoSeleccionadoInfo?.nombre ||
                                puestoSeleccionadoInfo?.nombre_punto}
                            </span>{' '}
                            se encuentra <span className="font-semibold">registrado</span>.
                          </>
                        );
                      }

                      // Caso registrado en otro punto
                      const lugarArmamento =
                        (resultadoSimulacion.detalleArmamento &&
                          (resultadoSimulacion.detalleArmamento.Lugar ||
                            resultadoSimulacion.detalleArmamento.lugar)) ||
                        'otro punto';

                      return (
                        <>
                          El armamento con serie{' '}
                          <span className="font-mono">{resultadoSimulacion.serieDetectada}</span> se encuentra{' '}
                          <span className="font-semibold">registrado</span>, pero asignado al punto{' '}
                          <span className="font-medium">{lugarArmamento}</span>.
                        </>
                      );
                    })()
                  ) : (
                    // Caso no registrado
                    <>
                      El armamento con serie{' '}
                      <span className="font-mono">{resultadoSimulacion.serieDetectada}</span>{' '}
                      <span className="font-semibold">no se encuentra registrado</span>.
                    </>
                  )}
                </p>
              </div>

              <div className="flex flex-col sm:flex-row sm:justify-end sm:space-x-3 space-y-2 sm:space-y-0">
                <button
                  type="button"
                  onClick={() => {
                    setShowResultadoModal(false);
                    resetFoto();
                    setPaso('captura');
                  }}
                  className="w-full sm:w-auto px-4 py-2 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Volver a tomar foto
                </button>
                <button
                  type="button"
                  onClick={resetFormulario}
                  className="w-full sm:w-auto px-4 py-2 rounded-lg border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50"
                >
                  Cancelar proceso
                </button>
                <button
                  type="button"
                  onClick={resetFormulario}
                  className="w-full sm:w-auto px-4 py-2 rounded-lg bg-green-600 text-white text-sm font-medium hover:bg-green-700"
                >
                  Aceptar
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default ArmamentoSupervision;

